<template>
  <div class="providers section-gradient-wrapper" data-name="providers-slider-section">
    <div class="flex items-center">
      <img class="categories-img" :src="require('src/assets/icon/svg/dots.svg')" />
      <div class="section__title">{{ $t('providers') }}</div>
      <a class="section__link cursor-pointer" data-name="providers-slider-all-link" @click="openSearchProvidersModal">
        {{ $t('all') }}
      </a>
      <ol-slider-button
        class="ol-ml-lg"
        :disabled-left="disabledLeftBtn"
        :disabled-right="disabledRightBtn"
        data-name="providers-slider-nav"
        @click-left="prevSlide"
        @click-right="nextSlide"
      />
    </div>

    <swiper
      v-if="manufacturers"
      v-bind="swiperOptions"
      :modules="[Grid, FreeMode]"
      data-name="providers-slider"
      @progress="onProgress"
      @swiper="onSwiper"
    >
      <swiper-slide v-for="provider in manufacturersList" :key="provider.objId">
        <provider-item :provider="provider" @toggle="toProviderGames(provider)" />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import getManufacturers from 'src/apollo/queries/getManufacturers.gql'
import { FreeMode, Grid } from 'swiper/modules'
import { useI18n } from 'vue-i18n'
import ProviderItem from 'components/providers/ProviderItem'
import { OlSliderButton } from '@gamesite/olimp-core'
import { useQuery } from '@vue/apollo-composable'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const progress = ref()
const manufacturers = ref(null)
const swiperInstance = ref(null)
const appModal = inject('appModal')
const group = computed(() => route.params.group)
const subgroup = computed(() => route.params.subgroup)

const disabledLeftBtn = computed(() => progress.value <= 0)
const disabledRightBtn = computed(() => progress.value >= 1)

const swiperOptions = {
  freeMode: {
    sticky: true,
  },
  slidesPerView: 3,
  slidesPerGroup: 3,
  spaceBetween: 8,
  breakpoints: {
    768: {
      slidesPerView: 5,
      slidesPerGroup: 5,
    },
    1024: {
      slidesPerView: 7,
      slidesPerGroup: 7,
    },
  },
}

async function getManufacturersByParams() {
  const variables = {}

  if (subgroup.value) {
    variables.subgroup = subgroup
  }
  if (group.value) {
    variables.group = group
  }

  const { onResult } = useQuery(getManufacturers, variables)
  onResult(({ data }) => (manufacturers.value = data?.manufacturerList ?? []))
}

getManufacturersByParams()

const manufacturersList = computed(() => {
  const lastItem = {
    code: 'lastAll',
    name: `${t('more')} ...`,
  }
  const manufacturersList = manufacturers.value
  const firstManufacturers = manufacturersList.slice(0, 20)
  const manufacturersWithList = [...firstManufacturers, lastItem]
  return manufacturersList.length > 20 ? manufacturersWithList : manufacturersList
})

function onSwiper(swiper) {
  swiperInstance.value = swiper
}

function onProgress(instance, progressValue) {
  progress.value = progressValue
}

function nextSlide() {
  swiperInstance.value.slideNext()
}

function prevSlide() {
  swiperInstance.value.slidePrev()
}

function openSearchProvidersModal() {
  appModal.open({
    componentName: defineAsyncComponent(() => import('components/providers/filter/SearchProvidersGames.vue')),
    isBigSize: true,
  })
}

function toProviderGames(provider) {
  if (provider.code === 'lastAll') {
    openSearchProvidersModal()
    return
  }
  const query = {}
  if (group.value) {
    query.group = group.value
  }
  if (subgroup.value) {
    query.subgroup = subgroup.value
  }
  router
    .push({
      name: 'GamesByManufacturer',
      params: {
        manufacturer: provider.code.toLowerCase(),
      },
      query,
    })
    .then(() => {
      window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
    })
}
</script>
